/**
 * Error monitoring and reporting utilities
 * Captures application errors and logs them to Firestore for analysis
 */

export interface ErrorLog {
  message: string;
  stack?: string;
  timestamp?: any;
  userId?: string;
  userAgent?: string;
  url?: string;
  context?: Record<string, any>;
  severity: 'error' | 'warning' | 'info';
}

/**
 * Log an error to Firestore
 */
export async function logError(error: ErrorLog): Promise<void> {
  try {
    const [{ db }, firestoreSdk] = await loadFirestoreModule();
    const errorsRef = firestoreSdk.collection(db, 'app-errors');
    const payload: Record<string, unknown> = {
      message: error.message,
      timestamp: firestoreSdk.serverTimestamp(),
      severity: error.severity,
      expiresAt: firestoreSdk.Timestamp.fromMillis(Date.now() + 30 * 86400000),
    };
    if (error.stack) payload.stack = error.stack;
    await firestoreSdk.addDoc(errorsRef, payload);
  } catch (err) {
    // Fail silently to avoid recursive error logging
    console.error('[ErrorMonitoring] Failed to log error:', err);
  }
}

/**
 * Set up global error handler
 */
export function setupGlobalErrorHandler(_userId?: string): void {
  if (typeof window === 'undefined') return;

  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    logError({
      message: event.message,
      stack: event.error?.stack,
      severity: 'error',
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    logError({
      message: error?.message || String(error),
      stack: error?.stack,
      severity: 'error',
    });
  });
}

/**
 * Log performance metrics
 */
export async function logPerformanceMetric(
  name: string,
  value: number,
  unit: string = 'ms',
  _userId?: string,
): Promise<void> {
  try {
    const [{ db }, firestoreSdk] = await loadFirestoreModule();
    const metricsRef = firestoreSdk.collection(db, 'app-performance');
    const payload: Record<string, unknown> = {
      name,
      value,
      unit,
      timestamp: firestoreSdk.serverTimestamp(),
      expiresAt: firestoreSdk.Timestamp.fromMillis(Date.now() + 30 * 86400000),
    };
    await firestoreSdk.addDoc(metricsRef, payload);
  } catch (err) {
    console.error('[PerformanceMonitoring] Failed to log metric:', err);
  }
}

let rumInitialized = false;
let firestoreModulePromise: Promise<[typeof import('./firebase'), typeof import('firebase/firestore')]> | null = null;

function loadFirestoreModule() {
  if (!firestoreModulePromise) {
    firestoreModulePromise = Promise.all([import('./firebase'), import('firebase/firestore')]);
  }
  return firestoreModulePromise;
}

/**
 * Capture a bounded first-load RUM sample without adding a third-party SDK.
 * Firebase Performance SDK adoption remains a separate release decision.
 */
export function initializeRealUserMonitoring(): void {
  if (rumInitialized || !import.meta.env.PROD || typeof window === 'undefined') return;
  rumInitialized = true;

  window.addEventListener(
    'load',
    () => {
      window.setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (navigation) {
          const metrics = [
            ['rum_ttfb', navigation.responseStart - navigation.requestStart],
            ['rum_dom_content_loaded', navigation.domContentLoadedEventEnd - navigation.startTime],
            ['rum_load', navigation.loadEventEnd - navigation.startTime],
          ] as const;
          metrics.forEach(([name, value]) => {
            if (Number.isFinite(value) && value >= 0) void logPerformanceMetric(name, value);
          });
        }

        const paintEntries = performance.getEntriesByType('paint');
        const firstContentfulPaint = paintEntries.find((entry) => entry.name === 'first-contentful-paint');
        if (firstContentfulPaint) void logPerformanceMetric('rum_fcp', firstContentfulPaint.startTime);
      }, 0);
    },
    { once: true },
  );

  if (typeof PerformanceObserver === 'undefined') return;
  let largestContentfulPaint: PerformanceEntry | undefined;
  const observer = new PerformanceObserver((list) => {
    largestContentfulPaint = list.getEntries().at(-1) || largestContentfulPaint;
  });

  try {
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
    window.setTimeout(() => {
      if (largestContentfulPaint) void logPerformanceMetric('rum_lcp', largestContentfulPaint.startTime);
      observer.disconnect();
    }, 2500);
  } catch {
    observer.disconnect();
  }
}

/**
 * Measure function execution time and log it
 */
export async function measurePerformance<T>(name: string, fn: () => Promise<T>, userId?: string): Promise<T> {
  const startTime = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - startTime;
    await logPerformanceMetric(name, duration, 'ms', userId);
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    await logPerformanceMetric(`${name}-error`, duration, 'ms', userId);
    throw error;
  }
}

/**
 * Get error summary for the last N hours
 */
export async function getErrorSummary(
  _hoursBack: number = 24,
): Promise<{ total: number; bySeverity: Record<string, number> }> {
  try {
    // This would typically query Firestore with a date range
    // For now, return a placeholder
    return { total: 0, bySeverity: { error: 0, warning: 0, info: 0 } };
  } catch (error) {
    console.error('[ErrorMonitoring] Failed to get error summary:', error);
    return { total: 0, bySeverity: {} };
  }
}
